import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { toast } from "sonner";
import { CalendarDays, AlertCircle, Grid3x3 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import MesJoursFeriesCalendarInfo from "../components/jours-feries/MesJoursFeriesCalendarInfo";
import MesJoursFeriesUpcomingHolidays from "../components/jours-feries/MesJoursFeriesUpcomingHolidays";
import MesJoursFeriesHolidaysList from "../components/jours-feries/MesJoursFeriesHolidaysList";
import MesJoursFeriesCalendarView from "../components/jours-feries/MesJoursFeriesCalendarView";

const MesJoursFeries = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("list");

  // Step 1: resolve employee record (null = no record, throws = other error)
  const employeeQ = useQuery({
    queryKey: ['portal-employee-me'],
    queryFn: async () => {
      try {
        const r = await apiClient.get("v1/employees/me");
        return r.data;
      } catch (err) {
        const status = err.response?.status ?? err.originalError?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
  });

  const employeeId = employeeQ.data?.id ?? null;
  const noEmployeeRecord = employeeQ.isSuccess && employeeQ.data === null;

  // Step 2: load calendar only once we have an employee ID
  const calendarQ = useQuery({
    queryKey: ['portal-my-holidays', employeeId],
    queryFn: async () => {
      try {
        const r = await apiClient.get(`v1/holidays/employee-calendars/employee/${employeeId}`);
        return r.data;
      } catch (err) {
        if (err.response?.status === 404) return null;
        toast.error(t("portal.holidays.loadError"));
        throw err;
      }
    },
    enabled: !!employeeId,
  });

  const calendarData = calendarQ.data;
  const calendar = calendarData?.calendar ?? null;
  const holidays = calendarData?.holidays ?? [];
  const upcomingHolidays = calendarData?.upcoming_holidays ?? [];
  const loading = employeeQ.isPending || (!!employeeId && calendarQ.isPending);

  const exportToICS = () => {
    if (!holidays || holidays.length === 0) {
      toast.error(t("portal.holidays.noHolidaysToExport"));
      return;
    }
    try {
      const icsEvents = holidays.map((holiday) => {
        const dateStr = new Date(holiday.date).toISOString().split("T")[0].replace(/-/g, "");
        return [
          "BEGIN:VEVENT",
          `DTSTART;VALUE=DATE:${dateStr}`,
          `DTEND;VALUE=DATE:${dateStr}`,
          `SUMMARY:${holiday.name}`,
          `DESCRIPTION:${holiday.description || ""}`,
          `LOCATION:${holiday.country || ""}`,
          "STATUS:CONFIRMED",
          "TRANSP:TRANSPARENT",
          "END:VEVENT",
        ].join("\r\n");
      });
      const icsContent = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Mawered//Public Holidays//EN",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        ...icsEvents,
        "END:VCALENDAR",
      ].join("\r\n");
      const blob = new Blob([icsContent], { type: "text/calendar" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `holidays-${calendar?.year || new Date().getFullYear()}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t("portal.holidays.exportSuccess"));
    } catch {
      toast.error(t("portal.holidays.exportError"));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (noEmployeeRecord) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("portal.holidays.title")}</h1>
          <p className="text-muted-foreground">{t("portal.holidays.subtitle")}</p>
        </div>
        <Card className="border-border bg-muted/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <h3 className="font-medium text-foreground">{t("portal.holidays.noEmployeeRecord")}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t("portal.holidays.noEmployeeRecordDesc")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!calendar) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("portal.holidays.title")}</h1>
          <p className="text-muted-foreground">{t("portal.holidays.subtitle")}</p>
        </div>
        <Card className="border-orange-900/40 bg-orange-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-foreground">{t("portal.holidays.noCalendar")}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t("portal.holidays.noCalendarDesc")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("portal.holidays.title")}</h1>
        <p className="text-muted-foreground">{t("portal.holidays.subtitle")}</p>
      </div>

      <MesJoursFeriesCalendarInfo calendar={calendar} holidays={holidays} exportToICS={exportToICS} />
      <MesJoursFeriesUpcomingHolidays upcomingHolidays={upcomingHolidays} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="list" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            {t("portal.holidays.list")} ({holidays.length})
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <Grid3x3 className="h-4 w-4" />
            {t("portal.holidays.calendarView")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6">
          <MesJoursFeriesHolidaysList holidays={holidays} />
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <MesJoursFeriesCalendarView holidays={holidays} calendar={calendar} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MesJoursFeries;
